import {
  HTMLChakraProps,
  ThemingProps,
  useMultiStyleConfig,
} from '@chakra-ui/react'
import type { MaskitoOptions } from '@maskito/core'
import { type ReactNode, Ref, useImperativeHandle, useRef } from 'react'

import { useDeepMemo } from '../../../hooks/useOnChange'
import {
  BaseInputStylesProvider,
  InputContainer,
  InputField,
  InputSideElement,
} from './BaseInput.parts'

export type BaseInputProps = Pick<
  ThemingProps<'BaseInput'>,
  'variant' | 'size'
> &
  HTMLChakraProps<'input'> & {
    leftInputElement?: ReactNode
    rightInputElement?: ReactNode
    maskOptions?: MaskitoOptions
    isInvalid?: boolean
  }

export const BaseInput = ({
  leftInputElement,
  rightInputElement,
  variant = 'primary',
  maskOptions,
  isInvalid,
  ref,
  ...props
}: BaseInputProps & { ref?: Ref<HTMLInputElement> }) => {
  const styles = useMultiStyleConfig('BaseInput', {
    ...props,
    variant,
    isInvalid,
  })

  const internalRef = useRef<HTMLInputElement>(null)

  const [inputProps, rootProps] = useSplitPropsByKeys(props, inputPropsKeys)

  useImperativeHandle(ref, () => internalRef?.current as any)

  return (
    <BaseInputStylesProvider value={styles}>
      <InputContainer
        inputFieldRef={internalRef}
        isInvalid={isInvalid}
        disabled={inputProps.disabled}
        isTextarea={props.as === 'textarea'}
        {...rootProps}
      >
        {leftInputElement ? (
          <InputSideElement side="left">{leftInputElement}</InputSideElement>
        ) : null}
        <InputField
          {...inputProps}
          ref={internalRef}
          maskOptions={maskOptions}
          isInvalid={isInvalid}
        />
        {rightInputElement ? (
          <InputSideElement side="right">{rightInputElement}</InputSideElement>
        ) : null}
      </InputContainer>
    </BaseInputStylesProvider>
  )
}

const splitPropsByKeys = <T extends Record<string, any>>(
  props: T,
  keys: (keyof T | (string & {}))[],
): [Pick<T, (typeof keys)[number]>, Omit<T, (typeof keys)[number]>] => {
  const pickedProps: any = {}
  const restProps: any = {}

  Object.entries(props).forEach(([key, value]) => {
    if (keys.includes(key as any)) {
      pickedProps[key] = value
    } else {
      restProps[key] = value
    }
  })

  return [pickedProps, restProps]
}

const useSplitPropsByKeys = <T extends Record<string, any>>(
  props: T,
  keys: (keyof T | (string & {}))[],
) => {
  return useDeepMemo(() => splitPropsByKeys(props, keys), [props, keys])
}

const inputPropsKeys: (keyof BaseInputProps | `data-${string}`)[] = [
  'onClick',
  'onChange',
  'value',
  'id',
  'placeholder',
  'disabled',
  'onFocus',
  'onBlur',
  'type',
  'name',
  'data-testid',
  'pattern',
  'autoFocus',
  'step',
  'required',
  'color',
  'min',
  'max',
  'minLength',
  'maxLength',
  'as',
  'resize',
  'autoComplete',
  'isInvalid',
  'style',
]
